import { useReducer } from "react"
import PropTypes from "prop-types"
import { chain, cloneDeep, find, isFunction, map, set, mapValues, get } from "lodash"
import { Button, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, ListSubheader } from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import classNames from "classnames"
import ChipSwitch from "./chip-switch"
import { METRICS_SHAPE, SUPPORTED_METRICS_SHAPE } from "helpers/metrics/shapes"
import { METRIC_TYPES } from "helpers/assets/constants"
import OPTooltip from "components/material/tooltip"

export default function CurrencyBasisDialog({ currentMetric, supportedMetrics, updateDefaultMetrics, onChange, closeDialog }) {
  const [defaultMetricUnitsWithOptions, setDefaultMetricUnitOption] = useReducer((state, action) => {
    const { metric, unit } = action
    const newState = cloneDeep(state)
    const newOptions = chain(newState)
      .get([metric, "options"])
      .map(option => ({
        ...option,
        isDefault: option.id === unit,
      }))
      .value()
    return set(newState, [metric, "options"], newOptions)
  }, supportedMetrics)

  function handleUpdate() {
    const defaultMetricUnits = mapValues(defaultMetricUnitsWithOptions, ({ options }) => get(find(options, "isDefault"), "id", METRIC_TYPES.NET_CONTRIBUTION))
    updateDefaultMetrics(defaultMetricUnits)

    // If the callee wants to update when changing the default unit
    if (currentMetric && isFunction(onChange)) {
      onChange(get(defaultMetricUnits, currentMetric))
    }
    closeDialog()
  }

  return <div className="currency-basis-dialog">
    <DialogTitle>
      Display preferences
    </DialogTitle>
    <DialogContent>
      <List disablePadding>
        <ListSubheader disableGutters>
          <h3>Default display</h3>
        </ListSubheader>
        <ListItem disableGutters>
          <ListItemText secondary="Select the preference for how each metric should be displayed" />
        </ListItem>
        {map(defaultMetricUnitsWithOptions, ({ label: metricLabel, options, disabled, upsell, tooltip }, metric) => <ListItem key={metric} disableGutters className={classNames("metric-item", { upsell })}>
          <ListItemText
            primary={<span className="item-text">
              {metricLabel}
              {tooltip && <OPTooltip title={tooltip} placement="bottom-start"><InfoOutlined /></OPTooltip>}
            </span>}
          />
          <ChipSwitch
            key={metric}
            metric={metric}
            options={options}
            disabled={disabled}
            setValue={(metric, unit) => {
              setDefaultMetricUnitOption({ metric, unit })
            }}
          />
        </ListItem>)}
      </List>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => closeDialog()}>Cancel</Button>
      <Button onClick={handleUpdate} color="primary">Update</Button>
    </DialogActions>
  </div>
}

CurrencyBasisDialog.propTypes = {
  currentMetric: METRICS_SHAPE,
  supportedMetrics: SUPPORTED_METRICS_SHAPE.isRequired,
  updateDefaultMetrics: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  closeDialog: PropTypes.func.isRequired,
}
